import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { CellValueChangedEvent } from 'ag-grid-community'
import type { OrderDetailDto, UpdateOrderPositionDto } from '@crm/shared'
import { updateOrderPosition } from '../../../shared/api/orders.api'
import { toast } from '../../../shared/ui/toastStore'
import type { PosRow } from '../components/orderPositionsColDefs'

/** Источник события для отката ячейки: такое изменение не сохраняем. */
const ROLLBACK = 'rollback'

function apiMessage(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
    ?.message
}

/**
 * Сохранение правки ячейки «Позиций заказа» (29.09.2026): артикул, бренд,
 * количество, цена клиенту → PATCH позиции заказа. Ответ — свежая карточка
 * заказа (счета, суммы, история), кладём её в кэш сразу. Ошибка сервера —
 * дословно в тост, ячейка откатывается перечитыванием заказа.
 */
export function useOrderPositionEdit(orderId: string) {
  const qc = useQueryClient()

  const save = useMutation({
    mutationFn: (vars: { requestItemId: string; patch: UpdateOrderPositionDto }) =>
      updateOrderPosition(orderId, vars.requestItemId, vars.patch),
    onSuccess: (detail: OrderDetailDto) => {
      qc.setQueryData(['orders', orderId], detail)
      void qc.invalidateQueries({ queryKey: ['orders'], exact: false, refetchType: 'none' })
      // Позиция заказа — это позиция заявки: карточка заявки перечитает таблицу.
      void qc.invalidateQueries({ queryKey: ['request-items-by-request'] })
      toast.success('Позиция изменена. Не забудьте заново скачать шаблон счёта для УНФ')
    },
    onError: (e) => {
      toast.error(apiMessage(e) ?? 'Не удалось изменить позицию заказа')
      void qc.invalidateQueries({ queryKey: ['orders', orderId] })
    },
  })

  const onCellValueChanged = useCallback(
    (e: CellValueChangedEvent<PosRow>) => {
      const field = e.colDef.field
      // Откат своей же ячейки — не правка: иначе уходил второй PATCH и ложный тост.
      if (e.source === ROLLBACK) return
      if (e.newValue === e.oldValue || !e.data) return
      const id = e.data.requestItemId
      if (field === 'quantity' || field === 'price') {
        const v = e.newValue as number
        const bad = typeof v !== 'number' || (field === 'quantity' ? v <= 0 : v < 0)
        if (bad) {
          e.node.setDataValue(field, e.oldValue, ROLLBACK)
          return
        }
        const patch: UpdateOrderPositionDto =
          field === 'price' ? { price: Math.round(v * 10_000) / 10_000 } : { quantity: v }
        save.mutate({ requestItemId: id, patch })
        return
      }
      if (field === 'partNumber') {
        const text = String(e.newValue ?? '').trim()
        if (!text) {
          toast.error('Артикул не может быть пустым')
          e.node.setDataValue(field, e.oldValue, ROLLBACK)
          return
        }
        save.mutate({ requestItemId: id, patch: { partNumber: text } })
        return
      }
      if (field === 'brand') {
        const text = String(e.newValue ?? '').trim()
        save.mutate({ requestItemId: id, patch: { brand: text || null } })
      }
    },
    [save],
  )

  return { onCellValueChanged, isSaving: save.isPending }
}
